// 网页端 i18n 初始化（约定见 docs/I18N.md）。i18next + react-i18next，资源静态打包（两种语言体积很小）。
// 语言协商：?lang= → 本机选择（localStorage idl_lang）→ 浏览器语言 → zh-CN。登录用户账号上的 preferred_language
// 由 AuthContext 在拿到用户后调用 applyUserLanguage 覆盖。
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/zh-tw';
import zhCommon from '../locales/zh-CN/common.json';
import zhAuth from '../locales/zh-CN/auth.json';
import zhHome from '../locales/zh-CN/home.json';
import zhSettings from '../locales/zh-CN/settings.json';
import zhErrors from '../locales/zh-CN/errors.json';
import zhListings from '../locales/zh-CN/listings.json';
import zhWidgets from '../locales/zh-CN/widgets.json';
import zhOrder from '../locales/zh-CN/order.json';
import zhPublish from '../locales/zh-CN/publish.json';
import zhVerification from '../locales/zh-CN/verification.json';
import zhWallet from '../locales/zh-CN/wallet.json';
import zhDashboard from '../locales/zh-CN/dashboard.json';
import zhProfile from '../locales/zh-CN/profile.json';
import zhMessages from '../locales/zh-CN/messages.json';
import enCommon from '../locales/en/common.json';
import enAuth from '../locales/en/auth.json';
import enHome from '../locales/en/home.json';
import enSettings from '../locales/en/settings.json';
import enErrors from '../locales/en/errors.json';
import enListings from '../locales/en/listings.json';
import enWidgets from '../locales/en/widgets.json';
import enOrder from '../locales/en/order.json';
import enPublish from '../locales/en/publish.json';
import enVerification from '../locales/en/verification.json';
import enWallet from '../locales/en/wallet.json';
import enDashboard from '../locales/en/dashboard.json';
import enProfile from '../locales/en/profile.json';
import enMessages from '../locales/en/messages.json';
import twCommon from '../locales/zh-TW/common.json';
import twAuth from '../locales/zh-TW/auth.json';
import twHome from '../locales/zh-TW/home.json';
import twSettings from '../locales/zh-TW/settings.json';
import twErrors from '../locales/zh-TW/errors.json';
import twListings from '../locales/zh-TW/listings.json';
import twWidgets from '../locales/zh-TW/widgets.json';
import twOrder from '../locales/zh-TW/order.json';
import twPublish from '../locales/zh-TW/publish.json';
import twVerification from '../locales/zh-TW/verification.json';
import twWallet from '../locales/zh-TW/wallet.json';
import twDashboard from '../locales/zh-TW/dashboard.json';
import twProfile from '../locales/zh-TW/profile.json';
import twMessages from '../locales/zh-TW/messages.json';

export const SUPPORTED = ['zh-CN', 'en', 'zh-TW'];
export const DEFAULT_LANG = 'zh-CN';
export const NAMESPACES = ['common', 'auth', 'home', 'settings', 'errors', 'listings', 'widgets', 'order', 'publish', 'verification', 'wallet', 'dashboard', 'profile', 'messages'];
const STORAGE_KEY = 'idl_lang';
const DAYJS_LOCALE = { 'zh-CN': 'zh-cn', en: 'en', 'zh-TW': 'zh-tw' };

/** 任意语言标签 → 支持的语言；不支持返回 null（与后端 utils/messages.js normalizeLocale 同规则）。 */
export function normalizeLang(tag) {
  if (!tag || typeof tag !== 'string') return null;
  const t = tag.trim().toLowerCase().replace(/_/g, '-');
  if (/^zh-(tw|hk|mo|hant)(-|$)/.test(t)) return 'zh-TW'; // 台港澳、zh-Hant 看繁体
  if (t === 'zh' || t.startsWith('zh-')) return 'zh-CN';
  if (t === 'en' || t.startsWith('en-')) return 'en';
  return null;
}

const readStored = () => {
  try {
    return normalizeLang(window.localStorage.getItem(STORAGE_KEY));
  } catch (e) {
    return null;
  }
};

export function detectLanguage() {
  let fromQuery = null;
  try {
    fromQuery = normalizeLang(new URLSearchParams(window.location.search).get('lang'));
  } catch (e) { /* 非浏览器环境 */ }
  if (fromQuery) return fromQuery;
  const stored = readStored();
  if (stored) return stored;
  const nav = typeof navigator !== 'undefined' ? (navigator.languages || [navigator.language]) : [];
  for (const l of nav) {
    const n = normalizeLang(l);
    if (n) return n;
  }
  return DEFAULT_LANG;
}

function syncSideEffects(lng) {
  dayjs.locale(DAYJS_LOCALE[lng] || 'zh-cn');
  if (typeof document !== 'undefined') document.documentElement.lang = lng;
}

i18n.use(initReactI18next).init({
  resources: {
    'zh-CN': { common: zhCommon, auth: zhAuth, home: zhHome, settings: zhSettings, errors: zhErrors, listings: zhListings, widgets: zhWidgets, order: zhOrder, publish: zhPublish, verification: zhVerification, wallet: zhWallet, dashboard: zhDashboard, profile: zhProfile, messages: zhMessages },
    'zh-TW': { common: twCommon, auth: twAuth, home: twHome, settings: twSettings, errors: twErrors, listings: twListings, widgets: twWidgets, order: twOrder, publish: twPublish, verification: twVerification, wallet: twWallet, dashboard: twDashboard, profile: twProfile, messages: twMessages },
    en: { common: enCommon, auth: enAuth, home: enHome, settings: enSettings, errors: enErrors, listings: enListings, widgets: enWidgets, order: enOrder, publish: enPublish, verification: enVerification, wallet: enWallet, dashboard: enDashboard, profile: enProfile, messages: enMessages },
  },
  lng: detectLanguage(),
  fallbackLng: DEFAULT_LANG,
  supportedLngs: SUPPORTED,
  ns: NAMESPACES,
  defaultNS: 'common',
  interpolation: { escapeValue: false }, // React 自己转义
  returnNull: false,
  react: { useSuspense: false },
});
syncSideEffects(i18n.language);
i18n.on('languageChanged', syncSideEffects);

/** 用户在本机切换语言：立即生效并记住。传 null = 清除本机选择、回到浏览器语言。 */
export function setLanguage(lng) {
  const n = normalizeLang(lng);
  try {
    if (n) window.localStorage.setItem(STORAGE_KEY, n);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch (e) { /* 存储不可用时只在本次会话生效 */ }
  return i18n.changeLanguage(n || detectLanguage());
}

/** 登录后按账号上的设置切换（账号设置优先于本机）；账号未设置则不动。 */
export function applyUserLanguage(user) {
  const n = normalizeLang(user && user.preferred_language);
  if (n && n !== i18n.language) setLanguage(n);
}

export default i18n;
